import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextEllipsisWithTooltip } from 'components/Kizen/Table';
import {
  SizedCell,
  SortableHeadCell,
  SubActionHeadCell,
} from 'components/Kizen/Table/CellLineHeightStyles';
import ValidatedField from 'components/Kizen/ValidatedField';
import DateTimeInputInline from 'components/Inputs/inline/DateTimeInput';
import { BasicInlineSelect } from 'components/Inputs/inline/Select';
import ActionCell from 'components/Tables/ActionCell';
import { toastVariant, useToast } from '__components/ToastProvider';
import { DisableActivityModal } from 'pages/ActivitySettings/DisableActivityModal';

const NO_VALUE = '—';

const numberWithTruncation = (value) => {
  return (
    <TextEllipsisWithTooltip>
      {!Number.isNaN(value) ? value : NO_VALUE}
    </TextEllipsisWithTooltip>
  );
};

const ActivitiesActionCell = ({ data, ...others }) => {
  const { t } = useTranslation();
  return (
    <ActionCell
      menuType="absolute"
      title={t('Edit Activity')}
      data={data}
      options={Object.values(ACTIVITY_ACTIONS(t))}
      {...others}
    />
  );
};

const ActivitiesEnabledCell = ({ activity, value, onUpdateField }) => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const [enabled, setEnabled] = useState(null);

  return (
    <>
      {enabled === false && (
        <DisableActivityModal
          activity={activity}
          onHide={() => setEnabled(null)}
          onConfirm={async (data) => {
            const payload = { enabled };

            if (data.action) {
              payload.disableOptions = {
                scheduledActivitiesAction: data.action,
                sendCalendarSyncEmails: data.sendCalendarSyncEmails,
              };

              if (data.destinationActivityId) {
                payload.disableOptions.destinationActivityId =
                  data.destinationActivityId;
              }
            }

            await onUpdateField(activity, payload);
            setEnabled(null);
          }}
        />
      )}
      <BasicInlineSelect
        isColored
        value={value}
        onChange={async (opt) => {
          if (opt.value) {
            try {
              await onUpdateField(activity, { enabled: true });
              showToast({
                message: t('Activity ‘{{name}}’ has been enabled.', {
                  name: activity.name,
                }),
              });
            } catch (error) {
              showToast({
                message: t(
                  'There was an issue enabling the activity. Please try again.'
                ),
                variant: toastVariant.FAILURE,
              });
            }
          } else {
            setEnabled(opt.value);
          }
        }}
        options={[
          {
            value: true,
            label: t('Enabled'),
            color: 'var(--color-feedback-toast-success)',
          },
          {
            value: false,
            label: t('Disabled'),
            color: 'var(--color-feedback-toast-warning)',
          },
        ]}
      />
    </>
  );
};

export const ACTIVITY_ACTIONS = (t) => ({
  Edit: { value: 'edit', label: t('Edit Activity') },
  Preview: { value: 'preview', label: t('Preview Activity') },
  Usage: { value: 'usage', label: t('Show Where Used') },
  Export: { value: 'export', label: t('Export Responses') },
  Duplicate: { value: 'duplicate', label: t('Duplicate') },
  Delete: { value: 'delete', label: t('Delete') },
});

export const getColumns = ({ onUpdateField, onSelectAction, t }) => [
  {
    id: 'spacer',
    headCell: <SizedCell width="20px" />,
    cell: <SizedCell width="20px" />,
  },
  {
    id: 'name',
    headCell: (
      <SortableHeadCell
        by="name"
        label={t('Activity Name')}
        width="400px"
        padding="200px"
      />
    ),
    cell: <SizedCell width="400px" padding="200px" />,
    format: (name, activity) => (
      <ValidatedField
        type="link"
        to={`/activities/${activity.id}`}
        value={name}
        onSubmit={(value) => {
          if (!value) {
            throw new Error('Activity names cannot be blank.');
          }
          if (activity.name === value) {
            return null;
          }
          return onUpdateField(activity, {
            name: value,
          });
        }}
      />
    ),
  },
  {
    id: 'enabled',
    headCell: (
      <SortableHeadCell label={t('Status')} width="150px" padding="20px" />
    ),
    cell: <SizedCell width="150px" padding="20px" />,
    format: (enabled, activity) => (
      <ActivitiesEnabledCell
        value={enabled}
        activity={activity}
        onUpdateField={onUpdateField}
      />
    ),
  },
  {
    id: 'nSubmissions',
    headCell: (
      <SortableHeadCell
        by="n_submissions"
        numeric
        label={t('Number of Submissions')}
        width="200px"
        padding="20px"
      />
    ),
    cell: <SizedCell width="200px" padding="20px" />,
    format: numberWithTruncation,
  },
  {
    id: 'created',
    headCell: (
      <SortableHeadCell
        numeric
        label={t('Date Created')}
        width="150px"
        padding="20px"
      />
    ),
    cell: <SizedCell width="150px" padding="20px" />,
    format: (created) => (
      <DateTimeInputInline readOnly value={created} showDateTooltip />
    ),
  },
  {
    id: 'actions',
    headCell: (
      <SubActionHeadCell width="50px" padding="16px" label={t('Actions')} />
    ),
    cell: (
      <ActivitiesActionCell
        width="50px"
        padding="16px"
        onSelectAction={onSelectAction}
      />
    ),
  },
];
